import type { FC } from "react"
import { useMemo } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { IconCheckOutline18 as Check } from "nucleo-ui-outline-18"
import { FixedSizeList } from "react-window"

import { cn } from "@productlane/lib/src/cn"
import { CommandItem } from "@productlane/ui/src/components/internal/command"
import { queries } from "@productlane/zero/queries"

import type { SharedFilterItemsProps, WithQuery } from "./types"
import { getProjectIcon } from "@/lib/projects"
import { useThreadsFilterState } from "../threads-filter-state"

export const LinkedTagsFilterItems: FC<SharedFilterItemsProps & WithQuery> = ({
	onSelect,
	query,
}) => {
	const { tagIds, removeTag, addTag } = useThreadsFilterState()

	const [tags] = useQuery(queries.tags())
	const [tagGroups] = useQuery(queries.tagGroups())

	const tagGroupColorMap = useMemo(() => {
		const map = new Map<string, string>()
		tagGroups.forEach((group) => {
			map.set(group.id, group.color)
		})
		return map
	}, [tagGroups])

	const filteredTags = useMemo(
		() =>
			tags.filter(({ name }) =>
				name.toLocaleLowerCase().includes(query.toLocaleLowerCase()),
			),
		[tags, query],
	)

	return (
		<>
			{filteredTags.length ? (
				<div className="h-64">
					<FixedSizeList
						height={256}
						width="100%"
						itemCount={filteredTags.length}
						itemData={filteredTags}
						itemKey={(index, data) => data[index]?.id ?? index}
						itemSize={36}
						overscanCount={5}
						className="!h-full"
					>
						{({ data, index, style }) => {
							const tag = data[index]

							if (!tag) return null

							const Icon = getProjectIcon(tag.icon)

							return (
								<CommandItem
									value={tag.name}
									onSelect={() => {
										if (tagIds.includes(tag.id)) {
											removeTag(tag.id)
										} else {
											addTag(tag.id)
										}
										onSelect()
									}}
									key={tag.id}
									style={style}
								>
									<div className="flex w-full items-center gap-2">
										<Icon
											className="shrink-0"
											style={{
												color: tagGroupColorMap.get(tag.tagGroupId),
											}}
										/>
										<span className="flex-1 truncate">{tag.name}</span>
										<Check
											className={cn("h-4 w-4 shrink-0", {
												"opacity-100": tagIds.includes(tag.id),
												"opacity-0": !tagIds.includes(tag.id),
											})}
										/>
									</div>
								</CommandItem>
							)
						}}
					</FixedSizeList>
				</div>
			) : (
				<div className="py-4 text-center text-sm">
					You haven&apos;t linked any tags yet
				</div>
			)}
		</>
	)
}
